import { getSafeForumRecoveryNext } from 'services/forumSessionRecovery';

type LoginLocation = {
  pathname?: string;
  search: string;
};

export type RedirectTarget = string | {
  pathname?: string;
  search?: string;
  hash?: string;
};

type LoginHistory = {
  location: {
    state?: {
      from?: RedirectTarget;
    };
  };
};

const BLOCKED_POST_LOGIN_PATHS = /^\/(?:login(?:\/|$)|logout(?:\/|$)|auth(?:\/|$)|api\/sso\/discourse(?:\/|$))/;

export const toRedirectPath = (target?: RedirectTarget): string | undefined => {
  if (!target) return undefined;
  if (typeof target === 'string') return target;
  return `${target.pathname ?? ''}${target.search ?? ''}${target.hash ?? ''}` || undefined;
};

export function isSafePostLoginRedirect(redirect: string): boolean {
  try {
    const origin = window.location.origin;
    const parsed = new URL(redirect, origin);
    const isExplicitRelativePath = redirect.startsWith('/')
      && !redirect.startsWith('//')
      && !redirect.startsWith('/\\');
    const isSameOriginAbsoluteUrl = /^[a-z][a-z\d+.-]*:/i.test(redirect)
      && parsed.origin === origin;
    const isDiscourseSsoLogin = parsed.pathname === '/login'
      && parsed.searchParams.get('sso_flow') === 'true';

    return (isExplicitRelativePath || isSameOriginAbsoluteUrl)
      && parsed.origin === origin
      && (isDiscourseSsoLogin || !BLOCKED_POST_LOGIN_PATHS.test(parsed.pathname));
  } catch {
    return false;
  }
}

export function resolvePostLoginRedirect(
  location: LoginLocation,
  history: LoginHistory,
  defaultRedirect = '/profile'
): string {
  const stateRedirect = toRedirectPath(history.location.state?.from);
  if (stateRedirect && isSafePostLoginRedirect(stateRedirect)) {
    return stateRedirect;
  }

  const params = new URLSearchParams(location.search);
  if (params.get('sso_flow') === 'true') {
    return `/login${location.search}`;
  }

  const recoveryNext = getSafeForumRecoveryNext(location.search);
  if (recoveryNext && isSafePostLoginRedirect(recoveryNext)) {
    return recoveryNext;
  }

  const redeemCode = params.get('redeemCode');
  if (redeemCode) {
    return `/profile?redeemCode=${encodeURIComponent(redeemCode)}`;
  }

  return defaultRedirect;
}
